import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { RegisterRequest } from '../types/auth.types';
import { BaseDialog } from './common/BaseDialog.tsx';
import api from '../services/api';

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
  role: 'client' | 'expert';
  title: string;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin, role, title }) => {
  const { register, loading, error, clearAuthError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    // name: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
    // name: '',
    terms: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // State for modals
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState<string | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogType, setDialogType] = useState<'terms' | 'privacy' | null>(null);
  const [confirmedDocs, setConfirmedDocs] = useState({ terms: false, privacy: false });

  useEffect(() => {
    if (confirmedDocs.terms && confirmedDocs.privacy) {
      setAgreedToTerms(true);
      if (formErrors.terms) {
        setFormErrors(prev => ({ ...prev, terms: '' }));
      }
    }
  }, [confirmedDocs, formErrors.terms]);

  const validateForm = () => {
    const errors = { email: '', password: '', name: '', terms: '' };
    let isValid = true;

    // if (!formData.name) {
    //   errors.name = 'Повне ім\'я є обов\'язковим';
    //   isValid = false;
    // }

    if (!formData.email) {
      errors.email = 'Email є обов\'язковим';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Невірний формат email';
      isValid = false;
    }

    if (!formData.password) {
      errors.password = 'Пароль є обов\'язковим';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'Пароль повинен містити мінімум 6 символів';
      isValid = false;
    }

    if (!agreedToTerms) {
      errors.terms = 'Ви повинні погодитися з умовами';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    clearAuthError();

    const registerData: RegisterRequest = {
      email: formData.email,
      password: formData.password,
      // name: formData.name,
      role: role,
    };

    await register(registerData);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) {
      clearAuthError();
    }

    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenDialog = async (e: React.MouseEvent, type: 'terms' | 'privacy') => {
    e.preventDefault();
    setDialogType(type);
    setDialogTitle(type === 'terms' ? 'Правила використання' : 'Політика конфіденційності');
    setDialogContent(null);
    setDialogError(null);
    setDialogLoading(true);
    setIsDialogOpen(true);

    try {
      const path = type === 'terms' ? '/information/pages/terms' : '/information/pages/privacy_policy';
      const response = await api.get(path, { headers: { 'Accept': 'text/html' } });
      setDialogContent(response.data);
    } catch (err) {
      console.error(`Failed to fetch ${type} content:`, err);
      setDialogError('Не вдалося завантажити вміст. Спробуйте пізніше.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setDialogContent(null);
    setDialogError(null);
    setDialogType(null);
  };

  const handleConfirmAndCloseDialog = () => {
    if (dialogType) {
      setConfirmedDocs(prev => ({ ...prev, [dialogType]: true }));
    }
    handleCloseDialog();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        {title}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 400 }}>
        {/* <TextField
          margin="normal"
          required
          fullWidth
          label="Повне ім'я"
          value={formData.name}
          onChange={handleChange('name')}
          autoComplete="name"
          autoFocus
          error={!!formErrors.name}
          helperText={formErrors.name}
          InputLabelProps={{ shrink: true }}
        /> */}

        <TextField
          margin="normal"
          required
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          autoComplete="email"
          error={!!formErrors.email}
          helperText={formErrors.email}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          autoComplete="new-password"
          error={!!formErrors.password}
          helperText={formErrors.password}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={<Checkbox checked={agreedToTerms} onChange={(e) => {
            setAgreedToTerms(e.target.checked);
            if (formErrors.terms) setFormErrors(prev => ({ ...prev, terms: '' }));
          }} />}
          label={
            <Typography variant="body2">
              Я погоджуюся з{' '}
              <Link component="button" variant="body2" onClick={(e) => handleOpenDialog(e, 'terms')} sx={{ cursor: 'pointer' }}>
                Правилами використання
              </Link>
              {' '}
              та
              {' '}
              <Link component="button" variant="body2" onClick={(e) => handleOpenDialog(e, 'privacy')} sx={{ cursor: 'pointer' }}>
                Політикою конфіденційності
              </Link>
            </Typography>
          }
          sx={{ mt: 1, mb: 2, mx: 'auto', maxWidth: 400, width: '100%' }}
        />
        {formErrors.terms && <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>{formErrors.terms}</Alert>}

        <Button type="submit" fullWidth variant="contained" sx={{ mt: 1, mb: 2 }} disabled={loading || !agreedToTerms}>
          {loading ? <CircularProgress size={24} /> : 'Зареєструватися'}
        </Button>

        {onSwitchToLogin && (
          <Box textAlign="center">
            <Typography variant="body2">
              Вже є акаунт?{' '}
              <Link component="button" variant="body2" onClick={(e) => { e.preventDefault(); onSwitchToLogin(); }}>
                Увійти
              </Link>
            </Typography>
          </Box>
        )}
      </Box>

      <BaseDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        title={dialogTitle}
        maxWidth="md"
      >
        {dialogLoading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
        {dialogError && <Alert severity="error">{dialogError}</Alert>}
        {dialogContent && (
          <>
            <div dangerouslySetInnerHTML={{ __html: dialogContent }} />
            <Box textAlign="center" sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Button onClick={handleConfirmAndCloseDialog} variant="contained">
                ОК
              </Button>
            </Box>
          </>
        )}
      </BaseDialog>
    </Box>
  );
};
